import { useMemoizedFn } from 'ahooks';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Button,
  Link,
  MessagePlugin,
  Popconfirm,
  Space,
  Table,
  Tooltip,
  Upload,
} from 'tdesign-react';
import { connect } from 'react-redux';
import { fetchEnvOptions, fetchK8sOptions } from '@/slices/resourceSlice';
import { clusterTypeOptions } from '@/utils/constant';
import { downloadFile, renderDateTime, renderOptions } from '@/utils/common';
import {
  clusterList,
  deleteCluster,
  exportCluster,
  importCluster,
} from '@/services/resourceService';
import useTable from '@/hooks/useTable';
import ListPanel from '@/components/ListPanel';
import PermWrapper from '@/components/PermWrapper';
import BuriedPermWrapper from '@/components/BuriedWrapper/BuriedPermWrapper';
import FormModal from './FormModal';

function ResourceCluster({ fetchEnvOptions, fetchK8sOptions }) {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visible, setVisible] = useState(false);
  const [currentRowId, setCurrentRowId] = useState();
  const navigate = useNavigate();

  /**
   * @type {import('tdesign-react/lib/table/type').BaseTableCol}
   */
  const columns = [
    {
      colKey: 'row-select',
      type: 'multiple',
      fixed: 'left',
      width: 46,
    },
    {
      colKey: 'name',
      title: '集群显示名称',
      ellipsis: true,
      width: 180,
    },
    {
      colKey: 'key',
      title: '集群名称',
      width: 180,
    },
    {
      colKey: 'type',
      title: '集群类型',
      cell: renderOptions,
      options: clusterTypeOptions,
      width: 120,
    },
    {
      colKey: 'desc',
      title: '集群描述',
      ellipsis: true,
      width: 220,
    },
    {
      colKey: 'envName',
      title: '归属环境',
      width: 140,
      ellipsis: true,
    },
    {
      colKey: 'stencilName',
      title: '变量模板',
      width: 140,
    },
    {
      title: '创建人',
      colKey: 'createBy',
      width: 150,
    },
    {
      title: '最后修改人',
      colKey: 'updateBy',
      width: 150,
    },
    {
      title: '创建时间',
      colKey: 'createTime',
      cell: renderDateTime,
      width: 180,
    },
    {
      title: '更新时间',
      colKey: 'updateTime',
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <Space>
          <BuriedPermWrapper
            resource='pulsar:resource:clusters:detail'
            eventId='RESOURCE_DETAIL_CLUSTER_DETAIL'
          >
            <Link
              theme='primary'
              hover='color'
              onClick={() => {
                navigate(`/maintenance/resource/cluster/detail/${row.id}`);
              }}
            >
              详情
            </Link>
          </BuriedPermWrapper>
          <PermWrapper resource='pulsar:resource:clusters:update'>
            <Link
              theme='primary'
              onClick={() => {
                setVisible(true);
                setCurrentRowId(row.id);
              }}
              hover='color'
            >
              编辑
            </Link>
          </PermWrapper>
          <PermWrapper resource='pulsar:resource:clusters:delete'>
            <Popconfirm theme='danger' content='确定删除？' onConfirm={() => doDelete(row)}>
              <Link theme='danger' hover='color'>
                删除
              </Link>
            </Popconfirm>
          </PermWrapper>
        </Space>
      ),
      width: 170,
    },
  ];

  const loadData = useMemoizedFn(async (params) => {
    setSelectedRowKeys([]);
    const { data } = await clusterList({ ...params, pageSize: 99999, startIndex: 1 }); // api分页无效，暂认为假分页
    setData(data.items);
  });

  const { header, config, setData, doSearch, doRefresh } = useTable({
    columns,
    operators: (
      <PermWrapper resource='pulsar:resource:clusters:create'>
        <Button
          theme='primary'
          onClick={() => {
            setVisible(true);
            setCurrentRowId(null);
          }}
        >
          新建集群
        </Button>
      </PermWrapper>
    ),
    onSearch: loadData,
  });

  const download = async () => {
    const { data } = await exportCluster({
      idList: selectedRowKeys,
    });
    downloadFile(new Blob([data], { type: 'application/json;charset=utf-8' }), '集群信息_');
  };

  const doUpload = async (file) => {
    if (file[0]) {
      const formData = new FormData();
      formData.append('file', file[0]);
      let uploadMsg;
      try {
        uploadMsg = MessagePlugin.loading({
          duration: 0,
          content: '正在上传...',
        });
        await importCluster(formData);
        MessagePlugin.success('上传成功！');
        doRefresh();
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        MessagePlugin.close(uploadMsg);
      }
    }
  };

  const doDelete = async ({ id }) => {
    try {
      await deleteCluster(id);
      MessagePlugin.success('删除成功');
      doRefresh();
    } catch (error) {
      console.trace(error);
      throw error;
    }
  };

  const handleClose = (refresh) => {
    setVisible(false);
    if (refresh) {
      doRefresh();
    }
  };

  useEffect(() => {
    doSearch();
    fetchEnvOptions();
    fetchK8sOptions();
  }, [doSearch, fetchEnvOptions, fetchK8sOptions]);

  return (
    <ListPanel
      header='集群信息'
      tip='集群对应一套Pulsar Cluster集群，主要包括zookeeper、bookie、broker等组件信息。'
      operators={
        <Space>
          <PermWrapper resource='pulsar:resource:clusters:export'>
            <Tooltip content={isEmpty(selectedRowKeys) ? '请先勾选要导出的集群' : null}>
              <BigButton
                theme='default'
                variant='outline'
                onClick={download}
                size='large'
                disabled={isEmpty(selectedRowKeys)}
              >
                批量导出
              </BigButton>
            </Tooltip>
          </PermWrapper>
          <PermWrapper resource='pulsar:resource:clusters:import'>
            <Upload theme='file' onSelectChange={doUpload}>
              <BigButton theme='default' variant='outline' size='large'>
                导入JSON文件
              </BigButton>
            </Upload>
          </PermWrapper>
        </Space>
      }
    >
      {header}
      <Table
        {...config}
        rowKey='id'
        selectedRowKeys={selectedRowKeys}
        onSelectChange={setSelectedRowKeys}
      />
      <FormModal visible={visible} id={currentRowId} handleClose={handleClose} />
    </ListPanel>
  );
}

export default connect(null, {
  fetchEnvOptions,
  fetchK8sOptions,
})(ResourceCluster);

const BigButton = styled(Button)`
  border: none;
  &&& {
    font: var(--td-font-body-medium);
  }
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1), 0px 1px 2px -1px rgba(0, 0, 0, 0.1);
`;
